import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { LayoutGroup, motion } from 'framer-motion';
import { type FC, useState } from 'react';

import Button from '@/components/Button';
import Text from '@/components/Text';
import { Switch } from '@/components/ui/switch';
import { usePatchExternalCandidate } from '@/hooks/usePatchExternalCandidate';
import { queries } from '@/queries';
import type { TransformApiCandidateResponse } from '@/services/externalCandidates';

type Props = {
  data: TransformApiCandidateResponse;
};

const Collections: FC<Props> = ({ data }) => {
  const [showAllCollections, setShowAllCollections] = useState(true);

  const { mutate } = usePatchExternalCandidate();
  const { data: collectionsData } = useQuery(
    queries.externalCandidates.collections
  );

  const handleDelete = (collectionId: number) =>
    mutate({
      id: data.id,
      data: {
        collections: data.collections
          .filter(
            ({ id: filteredCollectionId }) =>
              collectionId !== filteredCollectionId
          )
          .map(({ id: cId }) => cId),
      },
    });

  const handleAddToCollection = (collectionId: number) => {
    mutate({
      id: data.id,
      data: {
        collections: [
          ...data.collections.map(({ id: cId }) => cId),
          collectionId,
        ],
      },
    });
  };
  return (
    <>
      <div className="flex w-full items-center justify-between gap-2 border-b border-neutral-800 pb-2">
        <Text className="font-semibold">Collections</Text>
        <div className="flex flex-row gap-2">
          <Text variant="jb-body-small">Show all collections</Text>
          <Switch
            size="sm"
            checked={showAllCollections}
            onCheckedChange={setShowAllCollections}
          />
        </div>
      </div>
      <LayoutGroup>
        <motion.div className="grid grid-cols-2 gap-2">
          {data.collections.map(({ title, id }) => (
            <motion.div
              layout
              className="flex flex-row items-center justify-between rounded-lg border bg-neutral-100 px-6 py-2"
              key={id}
            >
              {title}
              <Button
                variant="unstyled"
                size="sm"
                className="text-error"
                prefixIcon={faTrash}
                onClick={() => handleDelete(id)}
              />
            </motion.div>
          ))}
          {showAllCollections &&
            collectionsData?.results
              .filter(
                ({ id: allCollectionsId }) =>
                  !data.collections
                    .map(({ id: userCollectionsId }) => userCollectionsId)
                    .includes(allCollectionsId)
              )
              .map(({ id, title }) => (
                <motion.div
                  layout
                  className="flex flex-row items-center justify-between rounded-lg border border-dashed border-primary-400 px-6 py-2"
                  key={id}
                >
                  {title}
                  <Button
                    variant="unstyled"
                    size="sm"
                    className="text-primary-600"
                    prefixIcon={faPlus}
                    onClick={() => handleAddToCollection(id)}
                  />
                </motion.div>
              ))}
        </motion.div>
      </LayoutGroup>
    </>
  );
};

export default Collections;
